import jQuery from 'jquery';

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaverOptions {
  accepts?: string | null;
  url: string;
  indicator?: string;
  errorIndicator?: string;
  hasSharedPath?: boolean;
  delay?: number;
  getParams: () => Record<string, any>;
  onStateChange?: (state: AutosaveState) => void;
}

class Autosaver {
  accepts: string | null;
  url: any;
  hasSharedPath: any;
  indicatorElement?: HTMLElement;
  errorIndicatorElement: HTMLElement;
  getParams: any;
  delay: any;
  saveTimer: ReturnType<typeof setTimeout> | null;
  onStateChange: AutosaverOptions['onStateChange'];

  constructor({
    accepts = 'application/json',
    url,
    indicator,
    errorIndicator,
    hasSharedPath,
    delay,
    getParams,
    onStateChange,
  }: AutosaverOptions) {
    this.accepts = accepts;
    this.url = url;
    this.hasSharedPath = hasSharedPath;
    this.onStateChange = onStateChange;

    let el = indicator && document.getElementById(indicator);
    if (el) {
      this.indicatorElement = el;
    }

    el = errorIndicator && document.getElementById(errorIndicator);
    if (el) {
      this.errorIndicatorElement = el;
    }

    this.getParams = getParams;

    this.delay = delay || 5000;
    this.saveTimer = null;
  }

  triggerSave() {
    if (!this.saveTimer) {
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        this._saveInBackground();
      }, this.delay);
    }
  }

  runTriggeredSave() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this._saveInBackground();
    }
  }

  _triggerStateChange(state: AutosaveState) {
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  _saveInBackground() {
    const csrfParam = document
      .querySelector('meta[name=csrf-param]')!
      .getAttribute('content') as string;
    const csrfToken = document
      .querySelector('meta[name=csrf-token]')!
      .getAttribute('content');

    const params = {
      [csrfParam]: csrfToken,
      reactive_client_id: window.reactiveClientId(),
      ...this.getParams(),
    };

    this._triggerStateChange('saving');

    jQuery.ajax({
      context: this,
      url: this.url,
      type: 'PUT',
      headers: this.hasSharedPath
        ? {
            Authorization: `Bearer ${window.sharingJWT}`,
          }
        : {},
      data: params,
      beforeSend: xhr => {
        if (this.accepts) {
          xhr.setRequestHeader('Accept', this.accepts);
        }
      },
      success: () => {
        this._triggerStateChange('saved');

        if (this.indicatorElement) {
          this.indicatorElement.classList.add('in');
        }
        setTimeout(() => {
          this._triggerStateChange('idle');

          if (this.indicatorElement) {
            this.indicatorElement.classList.remove('in');
          }
        }, 1000);
      },
      error: () => {
        this._triggerStateChange('error');

        if (this.errorIndicatorElement) {
          this.errorIndicatorElement.classList.add('in');
          if (this.indicatorElement) {
            this.indicatorElement.classList.remove('in');
          }
        }
        setTimeout(() => {
          this._triggerStateChange('idle');

          if (this.errorIndicatorElement) {
            this.errorIndicatorElement.classList.remove('in');
          }
        }, 10000);
      },
    });
  }
}

export default Autosaver;
